import { imageList } from "@/core/utils/imageList";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import Button from "@/core/components/button/Button";
import { useUnit } from "effector-react";
import { loginRequested, $authStore } from "../store/auth";
import "./LoginPage.scss";
import { Link } from "react-router-dom";
import GoogleLoginButton from "../components/GoogleLoginButton";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { VITE_PUBLIC_GOOGLE_LOGIN_CLIENT_ID } from "@/core/constants/env";
import Tracking from "@/core/utils/tracking";
import { ForgotPasswordModal } from "../components/ForgotPasswordModal";
import FacebookLoginButton from "../components/FacebookLoginButton";
import LinkedInLoginButton from "@/features/auth/components/LinkedInLoginButton";
import { SEO } from "@/core/components/SEO/SEO";

interface LoginFormData {
  username: string;
  password: string;
}

export const LoginPage = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const [authState, login] = useUnit([$authStore, loginRequested]);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(true);

  useEffect(() => {
    Tracking.pageView(
      "Login",
      location.pathname,
      undefined,
      undefined,
      undefined,
      document.title,
      undefined,
      undefined,
    );
  }, []);

  const onSubmit = (data: LoginFormData) => {
    // console.log("Form submitted with data:", data);
    // console.log("Login function:", login);
    // console.log("Auth state before login:", authState);
    login(data);
  };

  const handleResetPasswordClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowForgotPassword(true);
  };
  return (
    <div className="login-page">
      <SEO
        title={`Log in to Your Account | EUcalls`}
        description={`Log in to your EUcalls account to access European funding and partnership opportunities and stay updated on the latest open calls and news.`}
      />

      <div className="login-container">
        <div className="login-header">
          <Link to={"https://eucalls.net/"}>
            <img src={imageList.logo} alt="EU Calls" className="logo" />
          </Link>
          {showWelcomeBanner && (
            <div className="welcome-banner">
              <button
                className="close-banner"
                onClick={() => setShowWelcomeBanner(false)}
                aria-label="Close banner"
              >
                ×
              </button>
              <div className="welcome-banner-content">
                <h3>Welcome to the new, upgraded EUcalls experience.</h3>
                <p>
                  If you are a registered user already, please{" "}
                  <a
                    href="#"
                    onClick={handleResetPasswordClick}
                    className="reset-link"
                  >
                    reset your password
                  </a>{" "}
                  to continue.
                </p>
              </div>
            </div>
          )}
          <h1>Log in to your account</h1>
          <p>Welcome back! Please enter your details.</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <Controller
              name="username"
              control={control}
              rules={{
                required: "Email or username is required",
              }}
              render={({ field }) => (
                <InputText
                  {...field}
                  placeholder="Enter your email"
                  className={`email-input ${
                    errors.username ? "p-invalid" : ""
                  }`}
                />
              )}
            />
            {errors.username && (
              <small className="p-error">{errors.username.message}</small>
            )}
          </div>

          <div className="form-group">
            <Controller
              name="password"
              control={control}
              rules={{
                required: "Password is required",
                minLength: {
                  value: 4,
                  message: "Password must be at least 4 characters",
                },
              }}
              render={({ field }) => (
                <Password
                  {...field}
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className={`password-input ${
                    errors.password ? "p-invalid" : ""
                  }`}
                  toggleMask={true}
                  feedback={false}
                  style={{ width: "100%" }}
                />
              )}
            />

            {errors.password && (
              <small className="p-error">{errors.password.message}</small>
            )}
          </div>

          {authState.error && (
            <div className="error-message">
              {/* <small className="p-error">{authState.error}</small> */}
              <small className="p-error">{authState.error}</small>
            </div>
          )}

          <div className="social-login">
            <Button
              type="submit"
              className="sign-in-btn"
              variant="primary"
              size="large"
              loading={authState.loading}
              disabled={authState.loading}
              onClick={handleSubmit(onSubmit)}
            >
              {authState.loading ? "Signing in..." : "Sign in"}
            </Button>

            <GoogleOAuthProvider clientId={VITE_PUBLIC_GOOGLE_LOGIN_CLIENT_ID}>
              <GoogleLoginButton isLoginPage={true} />
            </GoogleOAuthProvider>
            <FacebookLoginButton isLoginPage={true} />
            <LinkedInLoginButton isLoginPage={true} />
          </div>

          {/* <div className="social-login">
            <Button
              className="social-btn facebook-btn"
              variant="secondary"
              size="large"
              outlined
            >
              <span className="social-icon">f</span>
              Sign in with Facebook
            </Button>

            <Button
              className="social-btn linkedin-btn"
              variant="secondary"
              size="large"
              outlined
            >
              <span className="social-icon">in</span>
              Sign in with LinkedIn
            </Button>
          </div> */}

          <div className="login-footer">
            <p>
              Don't have an account?{" "}
              <Link to="/signup" className="link">
                Sign up
              </Link>
            </p>
            <a
              href="#"
              className="link"
              onClick={(e) => {
                e.preventDefault();
                setShowForgotPassword(true);
              }}
            >
              Forgot password
            </a>
          </div>
        </form>
      </div>

      <ForgotPasswordModal
        visible={showForgotPassword}
        onHide={() => setShowForgotPassword(false)}
      />
    </div>
  );
};
